import { useEffect } from 'react'
import {
  type BookSize,
  BookPageDimensions,
  BookTextFonts,
  BookTextStyle,
  designedCovers,
} from '@mixtiles/web-backend-shared'
import type { PhotoBookCartItem } from 'stores/cartStore/CartItem'
import {
  fonts,
  getCoverFontUrl,
} from 'components/PhotoBook/CoverFontSelector/coverFonts'
import { BOOK_COVER_RENDERED_SIZE } from 'components/PhotoBook/PhotoBookView/Book3D/book.consts'
import { ReactComponent as CartBooks } from 'icons/assets/CartBooks.svg'
import {
  getPhotoPreviewUrl,
  isPrivatePhotoUrl,
} from 'components/PhotoBank/PhotoBank.utils'
import { PHOTO_PREVIEW_SIZES } from 'components/PhotoBank/PhotoBank.consts'
import { getCropStyle } from './cropStyle'
import * as S from './BookThumbnail.styles'

type Cover = PhotoBookCartItem['metadata']['cover']
type PhotoCover = Extract<Cover, { type: 'photo' }>
type DesignedCover = Extract<Cover, { type: 'designed' }>
type AiGeneratedCover = Extract<Cover, { type: 'ai-generated' }>

// MixtilesSans (Minimalist) and Caveat (Handwriting) ship in the global
// stylesheet, but Castoro/Bebas Neue are only fetched on demand. Load the
// chosen font's stylesheet lazily so the cart cover renders the right font
// without eagerly pulling every cover font.
function useCoverFont(textStyle: BookTextStyle) {
  useEffect(() => {
    const url = getCoverFontUrl(textStyle)
    if (!url || document.querySelector(`link[href="${url}"]`)) return

    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = url
    document.head.appendChild(link)
  }, [textStyle])
}

// The cover editor stores the title size as `fontHeight` (inches), relative to
// the page height. We reproduce it as a fraction of the cover and express it in
// container-query units so the cart title matches the book — including BOLD,
// whose fit-to-width scaling is already baked into the stored fontHeight.
// Falls back to the per-font base size when no fontHeight was computed.
function getTitleFontSize(
  coverFontHeight: number | undefined,
  size: BookSize,
  textStyle: BookTextStyle
) {
  const pageHeight = BookPageDimensions[size].pageHeight
  const fraction =
    coverFontHeight && coverFontHeight > 0
      ? coverFontHeight / pageHeight
      : fonts[textStyle].fontSize / BOOK_COVER_RENDERED_SIZE
  return `clamp(5px, ${(fraction * 100).toFixed(2)}cqw, 30px)`
}

type BookThumbnailProps = {
  item: PhotoBookCartItem
}

function BookThumbnail({ item }: BookThumbnailProps) {
  const { cover, size } = item.metadata

  return (
    <S.Book>
      {cover.type === 'designed' ? (
        <DesignedCoverThumbnail cover={cover} />
      ) : (
        <ImageCoverThumbnail cover={cover} size={size} />
      )}
    </S.Book>
  )
}

export default BookThumbnail

// Designed covers render the shared SVG artwork over its background color. The
// title is baked into the artwork, so there is no separate title overlay.
function DesignedCoverThumbnail({ cover }: { cover: DesignedCover }) {
  const { frontUrl, backgroundColor } = designedCovers[cover.designedCoverId]

  return (
    <>
      <S.DesignedCover style={{ backgroundColor }}>
        <S.CoverImage src={frontUrl} alt="Book Thumbnail" />
      </S.DesignedCover>
      <S.Spine aria-hidden />
    </>
  )
}

// Photo and AI-generated covers are both thumb-backed images; AI covers carry
// no overlaid title (it's baked into the generated image, so coverText is null).
function ImageCoverThumbnail({
  cover,
  size,
}: {
  cover: PhotoCover | AiGeneratedCover
  size: BookSize
}) {
  const { thumb } = cover
  // A private AI cover is stored by its original URL and shown through its preview
  const thumbUrl =
    cover.type === 'ai-generated' &&
    thumb.thumbUrl &&
    isPrivatePhotoUrl(thumb.thumbUrl)
      ? getPhotoPreviewUrl(thumb.thumbUrl, PHOTO_PREVIEW_SIZES.MIN)
      : thumb.thumbUrl

  return (
    <>
      {thumbUrl ? (
        <S.CoverClip>
          <S.CoverImage
            src={thumbUrl}
            style={getCropStyle(thumb.crop)}
            alt="Book Thumbnail"
          />
        </S.CoverClip>
      ) : (
        <S.CoverPlaceholder>
          <CartBooks />
        </S.CoverPlaceholder>
      )}
      <S.Spine aria-hidden />
      {thumb.coverText && (
        <CoverTitle
          text={thumb.coverText}
          textStyle={thumb.coverTextStyle ?? BookTextStyle.Minimalist}
          coverFontHeight={thumb.coverFontHeight}
          size={size}
        />
      )}
    </>
  )
}

function CoverTitle({
  text,
  textStyle,
  coverFontHeight,
  size,
}: {
  text: string
  textStyle: BookTextStyle
  coverFontHeight: number | undefined
  size: BookSize
}) {
  useCoverFont(textStyle)
  const { fontFamily, fontWeight } = BookTextFonts[textStyle]
  const fontSize = getTitleFontSize(coverFontHeight, size, textStyle)

  return (
    <>
      <S.BottomGradient aria-hidden />
      <S.Title style={{ fontSize, fontFamily, fontWeight }}>{text}</S.Title>
    </>
  )
}
